import { Injectable } from '@angular/core';
import { RegisterState, FirstStepData, SecondStepData } from '../models/register-state.interface';

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {

  constructor() { }

  /**
   * Save data to session storage
   */
  setItem(key: string, value: any): void {
    try {
      const serializedValue = JSON.stringify(value);
      sessionStorage.setItem(key, serializedValue);
    } catch (error) {
      // Error saving data
    }
  }

  /**
   * Get data from session storage
   */
  getItem<T>(key: string): T | null {
    try {
      const item = sessionStorage.getItem(key);
      if (item) {
        return JSON.parse(item) as T;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Remove item from session storage
   */
  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      // Error removing data
    }
  }

  /**
   * Clear all session storage
   */
  clear(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      // Error clearing session storage
    }
  }

  /**
   * Check if key exists in session storage
   */
  hasKey(key: string): boolean {
    try {
      return sessionStorage.getItem(key) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get all keys from session storage
   */
  getAllKeys(): string[] {
    try {
      const keys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      return [];
    }
  }

  /**
   * Get storage usage information
   */
  getStorageInfo(): any {
    try {
      const keys = this.getAllKeys();
      let totalSize = 0;
      const keySizes: { [key: string]: number } = {};

      keys.forEach(key => {
        const item = sessionStorage.getItem(key);
        if (item) {
          const size = new Blob([item]).size;
          keySizes[key] = size;
          totalSize += size;
        }
      });

      return {
        totalKeys: keys.length,
        totalSize: totalSize,
        totalSizeKB: (totalSize / 1024).toFixed(2),
        totalSizeMB: (totalSize / (1024 * 1024)).toFixed(4),
        keySizes: keySizes,
        keys: keys
      };
    } catch (error) {
      return {
        totalKeys: 0,
        totalSize: 0,
        totalSizeKB: '0',
        totalSizeMB: '0',
        keySizes: {},
        keys: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Save navigation history data
   */
  saveNavigationHistory(data: any): void {
    this.setItem('navigationServiceData', data);
  }

  /**
   * Get navigation history data
   */
  getNavigationHistory(): any {
    return this.getItem('navigationServiceData');
  }

  /**
   * Save user session data
   */
  saveUserSession(userData: any): void {
    this.setItem('userSession', userData);
  }

  /**
   * Get user session data
   */
  getUserSession(): any {
    return this.getItem('userSession');
  }

  /**
   * Clear user session data
   */
  clearUserSession(): void {
    this.removeItem('userSession');
  }

  /**
   * Save temporary form data
   */
  saveFormData(formName: string, formData: any): void {
    this.setItem(`form_${formName}`, formData);
  }

  /**
   * Get temporary form data
   */
  getFormData<T>(formName: string): T | null {
    return this.getItem<T>(`form_${formName}`);
  }

  /**
   * Clear temporary form data
   */
  clearFormData(formName: string): void {
    this.removeItem(`form_${formName}`);
  }

  /**
   * Save page state data
   */
  savePageState(pageName: string, stateData: any): void {
    this.setItem(`pageState_${pageName}`, stateData);
  }

  /**
   * Get page state data
   */
  getPageState<T>(pageName: string): T | null {
    return this.getItem<T>(`pageState_${pageName}`);
  }

  /**
   * Clear page state data
   */
  clearPageState(pageName: string): void {
    this.removeItem(`pageState_${pageName}`);
  }

  /**
   * Save cache data with expiration
   */
  setCacheItem(key: string, value: any, expirationMinutes: number = 30): void {
    const cacheData = {
      value: value,
      timestamp: new Date().getTime(),
      expirationMinutes: expirationMinutes
    };
    this.setItem(`cache_${key}`, cacheData);
  }

  /**
   * Get cache data (returns null if expired)
   */
  getCacheItem<T>(key: string): T | null {
    const cacheData = this.getItem<{
      value: T;
      timestamp: number;
      expirationMinutes: number;
    }>(`cache_${key}`);

    if (!cacheData) {
      return null;
    }

    const now = new Date().getTime();
    const expirationTime = cacheData.timestamp + (cacheData.expirationMinutes * 60 * 1000);

    if (now > expirationTime) {
      // Cache expired, remove it
      this.removeItem(`cache_${key}`);
      return null;
    }

    return cacheData.value;
  }

  /**
   * Clear expired cache items
   */
  clearExpiredCache(): void {
    const keys = this.getAllKeys();
    const cacheKeys = keys.filter(key => key.startsWith('cache_'));

    cacheKeys.forEach(key => {
      const cacheData = this.getItem<{
        value: any;
        timestamp: number;
        expirationMinutes: number;
      }>(key);

      if (cacheData) {
        const now = new Date().getTime();
        const expirationTime = cacheData.timestamp + (cacheData.expirationMinutes * 60 * 1000);

        if (now > expirationTime) {
          this.removeItem(key);
        }
      }
    });
  }

  /**
   * Get all cache keys
   */
  getCacheKeys(): string[] {
    const keys = this.getAllKeys();
    return keys.filter(key => key.startsWith('cache_')).map(key => key.replace('cache_', ''));
  }

  /**
   * Clear all cache data
   */
  clearAllCache(): void {
    const keys = this.getAllKeys();
    const cacheKeys = keys.filter(key => key.startsWith('cache_'));

    cacheKeys.forEach(key => {
      this.removeItem(key);
    });
  }

  // ==================== REGISTER STATE MANAGEMENT ====================

  private readonly REGISTER_STATE_KEY = 'register_state';
  private readonly REGISTER_FIRST_STEP_KEY = 'register_first_step';
  private readonly REGISTER_SECOND_STEP_KEY = 'register_second_step';
  private readonly REGISTER_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Save complete register state
   */
  saveRegisterState(state: RegisterState): void {
    const stateWithTimestamp = {
      ...state,
      timestamp: Date.now()
    };
    this.setItem(this.REGISTER_STATE_KEY, stateWithTimestamp);
  }

  /**
   * Get complete register state
   */
  getRegisterState(): RegisterState | null {
    const state = this.getItem<RegisterState & { timestamp: number }>(this.REGISTER_STATE_KEY);
    
    if (state && this.isRegisterStateValid(state)) {
      // Remove timestamp from returned state
      const { timestamp, ...registerState } = state;
      return registerState;
    }
    
    return null;
  }

  /**
   * Check if register state is still valid (not expired)
   */
  private isRegisterStateValid(state: any & { timestamp: number }): boolean {
    const now = Date.now();
    return (now - state.timestamp) < this.REGISTER_MAX_AGE;
  }

  /**
   * Clear complete register state
   */
  clearRegisterState(): void {
    this.removeItem(this.REGISTER_STATE_KEY);
    this.removeItem(this.REGISTER_FIRST_STEP_KEY);
    this.removeItem(this.REGISTER_SECOND_STEP_KEY);
  }

  /**
   * Save first step form data
   */
  saveRegisterFirstStep(data: FirstStepData): void {
    this.setItem(this.REGISTER_FIRST_STEP_KEY, {
      ...data,
      timestamp: Date.now()
    });
  }

  /**
   * Get first step form data
   */
  getRegisterFirstStep(): FirstStepData | null {
    const data = this.getItem<any>(this.REGISTER_FIRST_STEP_KEY);
    
    if (data && this.isRegisterStateValid(data)) {
      const { timestamp, ...firstStepData } = data;
      return firstStepData;
    }
    
    return null;
  }

  /**
   * Convert File to base64 string
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convert base64 string to File
   */
  private base64ToFile(base64String: string, fileName: string, mimeType: string): File {
    // Extract base64 data (remove data:image/png;base64, prefix if present)
    const base64Data = base64String.includes(',') 
      ? base64String.split(',')[1] 
      : base64String;
    
    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    
    return new File([blob], fileName, { type: mimeType });
  }

  /**
   * Serialize documents with files to base64
   */
  private async serializeDocuments(documents: any): Promise<any> {
    if (!documents) return documents;
    
    const serialized: any = {};
    for (const [key, doc] of Object.entries(documents)) {
      const docData = doc as any;
      if (docData && docData.file instanceof File) {
        // Only serialize file if it's not already accepted (accepted files are stored on server)
        if (docData.status !== 'ACCEPTED') {
          const base64 = await this.fileToBase64(docData.file);
          serialized[key] = {
            ...docData,
            file: null, // Remove file object
            fileBase64: base64,
            fileName: docData.file.name,
            fileType: docData.file.type,
            fileSize: docData.file.size
          };
        } else {
          // For accepted files, just save metadata without the file
          serialized[key] = {
            type: docData.type,
            status: docData.status,
            file: null
          };
        }
      } else {
        // Preserve document structure even without file
        serialized[key] = {
          type: docData?.type,
          status: docData?.status || 'EMPTY',
          file: null
        };
      }
    }
    return serialized;
  }

  /**
   * Deserialize documents from base64 to File objects
   */
  private deserializeDocuments(documents: any): any {
    if (!documents) return documents;
    
    const deserialized: any = {};
    for (const [key, doc] of Object.entries(documents)) {
      const docData = doc as any;
      if (docData && docData.fileBase64 && docData.fileName && docData.fileType) {
        try {
          // Restore file from base64
          const file = this.base64ToFile(
            docData.fileBase64,
            docData.fileName,
            docData.fileType
          );
          deserialized[key] = {
            type: docData.type,
            status: docData.status || 'PENDING',
            file: file
          };
        } catch (error) {
          // If file restoration fails, keep the document structure but mark as empty
          deserialized[key] = {
            type: docData.type,
            status: 'EMPTY',
            file: null
          };
        }
      } else if (docData) {
        // Preserve document structure even if no file
        deserialized[key] = {
          type: docData.type,
          status: docData.status || 'EMPTY',
          file: docData.file || null
        };
      }
    }
    return deserialized;
  }

  /**
   * Save second step form data
   */
  async saveRegisterSecondStep(data: SecondStepData): Promise<void> {
    // Serialize documents with files to base64
    const serializedData = { ...data };
    if (data.documents) {
      serializedData.documents = await this.serializeDocuments(data.documents);
    }
    
    this.setItem(this.REGISTER_SECOND_STEP_KEY, {
      ...serializedData,
      timestamp: Date.now()
    });
  }

  /**
   * Get second step form data
   */
  getRegisterSecondStep(): SecondStepData | null {
    const data = this.getItem<any>(this.REGISTER_SECOND_STEP_KEY);
    
    if (data && this.isRegisterStateValid(data)) {
      const { timestamp, ...secondStepData } = data;
      
      // Deserialize documents from base64 to File objects
      if (secondStepData.documents) {
        secondStepData.documents = this.deserializeDocuments(secondStepData.documents);
      }
      
      return secondStepData;
    }
    
    return null;
  }

  /**
   * Update register state with new step data
   */
  async updateRegisterState(step: number, stepData: FirstStepData | SecondStepData, currentStep: number, selectedRole: string): Promise<void> {
    const currentState = this.getRegisterState() || {
      currentStep: 0,
      selectedRole: '',
      timestamp: Date.now()
    };

    const updatedState: RegisterState = {
      ...currentState,
      currentStep,
      selectedRole,
      timestamp: Date.now()
    };

    if (step === 1) {
      updatedState.firstStepData = stepData as FirstStepData;
      this.saveRegisterFirstStep(stepData as FirstStepData);
    } else if (step === 2) {
      updatedState.secondStepData = stepData as SecondStepData;
      await this.saveRegisterSecondStep(stepData as SecondStepData);
    }

    this.saveRegisterState(updatedState);
  }

  /**
   * Get register progress information
   */
  getRegisterProgress(): {
    hasFirstStep: boolean;
    hasSecondStep: boolean;
    currentStep: number;
    selectedRole: string;
    isComplete: boolean;
  } {
    const state = this.getRegisterState();
    const firstStep = this.getRegisterFirstStep();
    const secondStep = this.getRegisterSecondStep();

    return {
      hasFirstStep: !!firstStep,
      hasSecondStep: !!secondStep,
      currentStep: state?.currentStep || 0,
      selectedRole: state?.selectedRole || '',
      isComplete: !!(firstStep && (state?.selectedRole === 'particulier' || secondStep))
    };
  }

  /**
   * Check if register data exists
   */
  hasRegisterData(): boolean {
    return this.hasKey(this.REGISTER_STATE_KEY) || 
           this.hasKey(this.REGISTER_FIRST_STEP_KEY) || 
           this.hasKey(this.REGISTER_SECOND_STEP_KEY);
  }

  /**
   * Get all register-related keys
   */
  getRegisterKeys(): string[] {
    return [
      this.REGISTER_STATE_KEY,
      this.REGISTER_FIRST_STEP_KEY,
      this.REGISTER_SECOND_STEP_KEY
    ].filter(key => this.hasKey(key));
  }

  /**
   * Clear expired register data
   */
  clearExpiredRegisterData(): void {
    const keys = this.getRegisterKeys();
    
    keys.forEach(key => {
      const data = this.getItem<any>(key);
      if (data && !this.isRegisterStateValid(data)) {
        this.removeItem(key);
      }
    });
  }

  /**
   * Check if user should be redirected to a specific step on page load
   */
  shouldRedirectToStep(): { shouldRedirect: boolean; step: number; role: string } {
    const state = this.getRegisterState();
    
    if (state && state.currentStep > 0 && state.selectedRole) {
      return {
        shouldRedirect: true,
        step: state.currentStep,
        role: state.selectedRole
      };
    }
    
    return {
      shouldRedirect: false,
      step: 0,
      role: ''
    };
  }

  /**
   * Get the current step the user should be on
   */
  getCurrentStep(): number {
    const state = this.getRegisterState();
    return state?.currentStep || 0;
  }

  /**
   * Get the current role the user has selected
   */
  getCurrentRole(): string {
    const state = this.getRegisterState();
    return state?.selectedRole || '';
  }

  /**
   * Get a user-friendly message about their registration progress
   */
  getRegistrationProgressMessage(): string {
    const progress = this.getRegisterProgress();
    
    if (!progress.hasFirstStep && !progress.hasSecondStep) {
      return '';
    }
    
    if (progress.selectedRole === 'particulier' && progress.hasFirstStep) {
      return 'Vous avez une inscription en cours. Voulez-vous continuer?';
    }
    
    if (progress.selectedRole === 'prestataire') {
      if (progress.hasFirstStep && !progress.hasSecondStep) {
        return 'Vous avez commencé votre inscription en tant que prestataire. Voulez-vous continuer?';
      } else if (progress.hasFirstStep && progress.hasSecondStep) {
        return 'Votre inscription de prestataire est presque terminée. Voulez-vous continuer?';
      }
    }
    
    return 'Vous avez une inscription en cours. Voulez-vous continuer?';
  }

  /**
   * Check if user has any incomplete registration data
   */
  hasIncompleteRegistration(): boolean {
    const progress = this.getRegisterProgress();
    return progress.hasFirstStep || progress.hasSecondStep;
  }
}
